import { useCallback, useEffect, useRef } from 'react';
import useSWR, { SWRConfiguration, SWRResponse } from 'swr';
import { useClient } from './useClient';
import { useOrganizationContext } from '../context/OrganizationContext';
import { SurveyFormView, SurveyLaunch, SurveyStatistics } from '../types/survey';

export const SURVEY_REFRESH_INTERVAL_MS = 15000;

const SURVEY_SWR_OPTIONS: SWRConfiguration = {
  refreshInterval: SURVEY_REFRESH_INTERVAL_MS,
  keepPreviousData: true,
};

export function surveysBasePath(organizationId: string): string {
  return `/organizations/${organizationId}/surveys`;
}

const STATIC_SWR_OPTIONS: SWRConfiguration = { ...SURVEY_SWR_OPTIONS, refreshInterval: 0 };

/** `suffix` null: no request. */
function useSurveyResource<T>(suffix: string | null, live = true): SWRResponse<T | undefined> {
  const { organization } = useOrganizationContext();
  const { get } = useClient();
  const fetcher = useCallback((url: string): Promise<T | undefined> => get<T>(url), [get]);
  const key = organization && suffix !== null ? `${surveysBasePath(organization.id)}/${suffix}` : null;
  return useSWR<T | undefined>(key, fetcher, live ? SURVEY_SWR_OPTIONS : STATIC_SWR_OPTIONS);
}

export function useSurveyForm(): SWRResponse<SurveyFormView | undefined> {
  return useSurveyResource<SurveyFormView>('form');
}

export function useSurveyLaunches(): SWRResponse<SurveyLaunch[] | undefined> {
  return useSurveyResource<SurveyLaunch[]>('launches');
}

/**
 * D-A11: the statistics of one round; no request without a round (all rounds would bring up to 5000 texts).
 * Only an active round can get new answers, so only its statistics are polled. A closed round is read once when it is
 * shown (focus does not read it again: revalidateOnFocus is off in the global SWR config of App.tsx). When the shown
 * round closes, it is read one last time to catch the last answers.
 */
export function useSurveyStatistics(launchId: string | undefined, live = true): SWRResponse<SurveyStatistics | undefined> {
  const suffix = launchId ? `statistics?launchId=${encodeURIComponent(launchId)}` : null;
  const response = useSurveyResource<SurveyStatistics>(suffix, live);
  const previous = useRef({ launchId, live });
  const { mutate } = response;

  useEffect(() => {
    // Only when the same round closes: a switch to another round already reads it through the new key
    if (previous.current.live && !live && previous.current.launchId === launchId) void mutate();
    previous.current = { launchId, live };
  }, [launchId, live, mutate]);

  return response;
}
